// 3D model view (photogrammetry mesh · three.js + OrbitControls).
// Streams the GLB from the survey CDN with a progress loader, then hands the
// scene to OrbitControls: drag = orbit, scroll / pinch = zoom, right-drag /
// two-finger = pan. Buttons mirror the map's zoom stack + fit + auto-orbit.
// GLB only — the OBJ path (giant projected-CRS coords, float32 shimmer,
// Z-up rotation fix) is retired; glTF is Y-up by spec and exports in a
// local frame.
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { IcCube, IcMaximize, IcMinus, IcPlus, IcUndo, IcWarnTri, type DIconProps } from "./icons";
import type { ReactNode } from "react";


interface LoadStatus {
  state: "loading" | "ready" | "error";
  pct?: number | null;
  mb?: number;
}

interface ViewerApi {
  zoom: (f: number) => void;
  fit: () => void;
  setAutoRotate: (v: boolean) => void;
}

export function Model3DView({ url }: { url: string }) {
  const mountRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<ViewerApi | null>(null);
  const [status, setStatus] = useState<LoadStatus>({ state: "loading", pct: 0, mb: 0 });
  const [autoRotate, setAutoRotate] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    let disposed = false, raf: number | null = null;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b0f16);
    const camera = new THREE.PerspectiveCamera(50, el.clientWidth / Math.max(1, el.clientHeight), 0.1, 100000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(el.clientWidth, el.clientHeight);
    el.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotateSpeed = 1.4;
    controls.screenSpacePanning = false;

    scene.add(new THREE.HemisphereLight(0x9aa4b2, 0x16202e, 0.95));
    const sun = new THREE.DirectionalLight(0xffffff, 0.85);
    sun.position.set(1, 1.5, 0.8);
    scene.add(sun);
    const fill = new THREE.DirectionalLight(0x6a7688, 0.35);
    fill.position.set(-1, 0.6, -1);
    scene.add(fill);

    const home = { pos: new THREE.Vector3(), tgt: new THREE.Vector3() };
    const goHome = () => { camera.position.copy(home.pos); controls.target.copy(home.tgt); controls.update(); };

    apiRef.current = {
      zoom: (f: number) => {
        const v = new THREE.Vector3().subVectors(camera.position, controls.target).multiplyScalar(f);
        camera.position.copy(controls.target).add(v);
        controls.update();
      },
      fit: () => goHome(),
      setAutoRotate: (v: boolean) => { controls.autoRotate = v; },
    };

    setStatus({ state: "loading", pct: 0, mb: 0 });
    const loader = new GLTFLoader();
    // The pipeline compresses meshes with meshoptimizer
    // (EXT_meshopt_compression) — the decoder must be registered or parsing
    // fails outright.
    loader.setMeshoptDecoder(MeshoptDecoder);
    loader.load(url, (gltf) => {
      if (disposed) return;
      const obj = gltf.scene;
      obj.traverse((c) => {
        const mesh = c as THREE.Mesh;
        if (!mesh.isMesh) return;
        // Render the photogrammetry capture unlit so the model looks like the
        // site: prefer the GLB's baked texture, then per-vertex colors; the
        // gray clay material is only the fallback for colorless meshes.
        const prev = mesh.material as THREE.MeshStandardMaterial | undefined;
        const hasColors = (mesh.geometry as THREE.BufferGeometry).hasAttribute?.("color");
        if (prev?.map) mesh.material = new THREE.MeshBasicMaterial({ map: prev.map, side: THREE.DoubleSide });
        else if (hasColors) mesh.material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
        else mesh.material = new THREE.MeshStandardMaterial({ color: 0x9aa4b2, roughness: 0.92, metalness: 0.05, side: THREE.DoubleSide, flatShading: true });
      });
      // glTF is Y-up by spec and the pipeline exports a local frame (small
      // coordinates — no float32 shimmer, no Z-up rotation needed). Just
      // measure, center and sit the model on the ground grid.
      obj.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(obj);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      obj.position.sub(center);
      obj.position.y += size.y / 2; // base sits on the ground grid
      scene.add(obj);

      const maxDim = Math.max(size.x, size.y, size.z) || 10;
      const grid = new THREE.GridHelper(maxDim * 3, 36, 0x1e2940, 0x141c29);
      grid.position.y = -0.02 * maxDim;
      scene.add(grid);

      controls.minDistance = maxDim * 0.15;
      controls.maxDistance = maxDim * 6;
      controls.maxPolarAngle = Math.PI * 0.495; // don't go under the ground
      // Match the depth range to the model. The default 0.1 / 100000 is a
      // ~1e6 near:far ratio that starves the depth buffer and lets close
      // surfaces z-fight (shimmer); tie it to the model's real scale instead.
      camera.near = maxDim * 0.04;
      camera.far = maxDim * 40;
      camera.updateProjectionMatrix();
      home.pos.set(maxDim * 0.85, maxDim * 0.55, maxDim * 0.85);
      home.tgt.set(0, size.y * 0.35, 0);
      goHome();
      setStatus({ state: "ready" });
    }, (ev) => {
      if (disposed) return;
      setStatus({ state: "loading", pct: ev.total ? Math.round((ev.loaded / ev.total) * 100) : null, mb: ev.loaded / 1048576 });
    }, () => { if (!disposed) setStatus({ state: "error" }); });

    const ro = new ResizeObserver(() => {
      camera.aspect = el.clientWidth / Math.max(1, el.clientHeight);
      camera.updateProjectionMatrix();
      renderer.setSize(el.clientWidth, el.clientHeight);
    });
    ro.observe(el);

    const tick = () => { raf = requestAnimationFrame(tick); controls.update(); renderer.render(scene, camera); };
    tick();

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
      apiRef.current = null;
    };
  }, [retryTick, url]);

  useEffect(() => { if (apiRef.current) apiRef.current.setAutoRotate(autoRotate); }, [autoRotate]);

  const ready = status.state === "ready";
  return (
    <div style={{ position: "absolute", inset: 0, background: "#0b0f16" }}>
      <div ref={mountRef} style={{ position: "absolute", inset: 0 }}></div>

      {status.state === "loading" &&
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, zIndex: 20 }}>
          <span className="spin" style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.14)", borderTopColor: "var(--di-orange)" }}></span>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 9 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--di-text)" }}>Loading 3D model…</span>
            {status.pct != null
              ? <div style={{ width: 240, height: 5, borderRadius: 999, background: "var(--di-ink)", overflow: "hidden" }}>
                <div style={{ width: (status.pct || 0) + "%", height: "100%", background: "var(--di-orange)", borderRadius: 999, transition: "width 200ms ease" }}></div>
              </div>
              : null}
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--di-subtle)" }}>
              {status.pct != null ? status.pct + "%" : ""}{status.mb ? (status.pct != null ? " · " : "") + status.mb.toFixed(1) + " MB" : ""}
            </span>
          </div>
        </div>}

      {status.state === "error" &&
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 15, zIndex: 20, textAlign: "center", padding: 24 }}>
          <div style={{ width: 52, height: 52, borderRadius: 13, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.26)", display: "flex", alignItems: "center", justifyContent: "center" }}><IcWarnTri size={22} color="var(--di-destructive)" /></div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--di-text)", marginBottom: 5 }}>Couldn't load the 3D model</div>
            <div style={{ fontSize: 12.5, color: "var(--di-subtle)", maxWidth: 320, lineHeight: 1.55 }}>The mesh failed to download. Check your connection and try again.</div>
          </div>
          <button onClick={() => setRetryTick((t) => t + 1)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 9, border: "none", background: "var(--di-orange)", color: "#160d03", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600 }}><IcUndo size={14} color="#160d03" />Retry</button>
        </div>}

      {/* control stack — mirrors the map's zoom controls */}
      <div style={{ position: "absolute", right: 14, bottom: 14, display: "flex", flexDirection: "column", background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 10, overflow: "hidden", zIndex: 30, opacity: ready ? 1 : 0.4, pointerEvents: ready ? "auto" : "none" }}>
        <CtlBtn Ic={IcPlus} label="Zoom in" onClick={() => apiRef.current && apiRef.current.zoom(0.78)} />
        <div style={{ height: 1, background: "var(--di-border)" }}></div>
        <CtlBtn Ic={IcMinus} label="Zoom out" onClick={() => apiRef.current && apiRef.current.zoom(1.28)} />
        <div style={{ height: 1, background: "var(--di-border)" }}></div>
        <CtlBtn Ic={IcMaximize} label="Fit model" onClick={() => apiRef.current && apiRef.current.fit()} />
        <div style={{ height: 1, background: "var(--di-border)" }}></div>
        <CtlBtn Ic={IcOrbit} label={autoRotate ? "Stop auto-orbit" : "Auto-orbit"} active={autoRotate} onClick={() => setAutoRotate((v) => !v)} />
      </div>

      {/* gesture hints */}
      {ready &&
        <div style={{ position: "absolute", left: 14, bottom: 14, zIndex: 30, display: "flex", alignItems: "center", gap: 14, padding: "9px 13px", background: "rgba(14,18,25,0.86)", border: "1px solid var(--di-border)", borderRadius: 10, backdropFilter: "blur(8px)" }}>
          <GestureHint k="Drag" v="Orbit" />
          <GestureHint k="Scroll" v="Zoom" />
          <GestureHint k="Right-drag" v="Pan" />
        </div>}

    </div>
  );
}

function CtlBtn({ Ic, label, onClick, active }: { Ic: (p: DIconProps) => ReactNode; label: string; onClick: () => void; active?: boolean }) {
  const [h, setH] = useState(false);
  const col = active ? "var(--di-orange)" : h ? "var(--di-text)" : "var(--di-muted-2)";
  return (
    <button onClick={onClick} title={label} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ width: 34, height: 34, border: "none", background: active ? "rgba(249,115,22,0.14)" : h ? "rgba(255,255,255,0.05)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
      <Ic size={15} color={col} />
    </button>
  );
}
function GestureHint({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--di-text)", padding: "2px 6px", borderRadius: 5, background: "var(--di-ink)", border: "1px solid var(--di-border)" }}>{k}</span>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--di-subtle)" }}>{v}</span>
    </div>
  );
}
// orbit icon (circular arrows around a sphere)
function IcOrbit(p: DIconProps) {
  return <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" stroke={p.color || "currentColor"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3.2" /><path d="M20.3 9.6A9 9 0 0 0 4.9 6.4" /><path d="M3.7 14.4a9 9 0 0 0 15.4 3.2" /><polyline points="20.6 5.4 20.3 9.6 16.2 9.2" /><polyline points="3.4 18.6 3.7 14.4 7.8 14.8" /></svg>;
}
